import {
    IDepositRemainderResponse,
    IDepositResponse,
    IPaymentsCurrencyConfig,
    IWithdrawalResponse,
} from "@finbackoffice/clientbff-client";
import {
    DecardPaymentMethod,
    DepositStatus,
    DocumentStatus,
    DocumentType,
    PaymentProvider,
    WalletType,
} from "@finbackoffice/enums";
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { sortAlphabeticallyByProp } from "@finbackoffice/fe-core";
import { useDepositWithdrawalCountLimitsV2 } from "hooks/pix-wallet";
import {
    MultiWalletViewType,
    MultiWalletPaymentMethod,
    round,
    convertProviderConfig,
    useRuntimeConfig,
    useUserDocuments,
} from "@finbackoffice/site-core";
import { ConfigContextV2 } from "./config-v2.provider";
import { ExchangeRatesContextV2 } from "./exchange-rates-v2.provider";
import { WalletsContext } from "./wallets.provider";

type MultiWalletStepType = "payment_methods" | "form" | "status";

type MultiWalletDepositStepType = "payment_methods" | "form" | "iframe" | "status";

interface IMultiWalletDataContext extends Props {
    activeView: MultiWalletViewType;
    setActiveView: Dispatch<SetStateAction<MultiWalletViewType>>;
    step: MultiWalletStepType | MultiWalletDepositStepType;
    setStep: (step: MultiWalletStepType | MultiWalletDepositStepType) => void;
    depositResponse: IDepositResponse | null;
    setDepositResponse: (val: IDepositResponse | null) => void;
    withdrawResponse: IWithdrawalResponse | null;
    setWithdrawResponse: Dispatch<SetStateAction<IWithdrawalResponse | null>>;
    onDepositSocketUpdate: (deposit: IDepositResponse, error: any) => void;
    selectedPaymentMethod: DecardPaymentMethod | "";
    setSelectedPaymentMethod: Dispatch<SetStateAction<DecardPaymentMethod | "">>;
    selectedProvider: "" | PaymentProvider;
    setSelectedProvider: Dispatch<SetStateAction<"" | PaymentProvider>>;
    depositPaymentMethods: MultiWalletPaymentMethod[];
    withdrawPaymentMethods: MultiWalletPaymentMethod[];
    amountKeyboardValues: {
        deposit: { label: string; value: string }[];
        withdraw: { label: string; value: string }[];
    };
    minAmount: {
        deposit?: number;
        withdraw?: number;
    };
    maxAmount: {
        deposit: number;
        withdraw: number;
    };
    remainder: {
        shownDepositCounter: boolean;
        shownWithdrawalCounter: boolean;
        nextUpdateDepositCount: number;
        nextUpdateWithdrawalCount: number;
        depositRemainderResponse: IDepositRemainderResponse | undefined;
        isWithdrawalPendingLimit: boolean;
    };
    rejectedVerificationMethods?: {
        deposit: DocumentType[];
        withdraw: DocumentType[];
    };
    documentsLoading: boolean;
    currentCurrencyConfig: IPaymentsCurrencyConfig | undefined;
}
export const MultiWalletDataContext = createContext<IMultiWalletDataContext>(null as any);

type Props = {
    onClose: () => void;
};

export const MultiWalletDataProvider: FC<Props & PropsWithChildren> = ({ children, onClose }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { currentCurrency, userWallets } = useContext(WalletsContext);
    const {
        depositRemainderResponse,
        withdrawalRemainderResponse,
        nextUpdateWithdrawalCount,
        nextUpdateDepositCount,
    } = useDepositWithdrawalCountLimitsV2();
    const {
        decardPaymentConfig,
        gumBallPayPaymentConfig,
        sitePaymentsCurrencyConfig,
        siteRegistrationConfig,
    } = useContext(ConfigContextV2);
    const { usdExchangeRate } = useContext(ExchangeRatesContextV2);
    const { docStatus, loading: documentsLoading } = useUserDocuments();
    const walletWithdrawAmountKeyboardValues =
        COMMON_SITE_CONFIGS.wallet.withdraw.amountKeyboardValues;
    const walletDepositAmountKeyboardValues =
        COMMON_SITE_CONFIGS.wallet.deposit.amountKeyboardValues;
    const currentCurrencyConfig = currentCurrency
        ? sitePaymentsCurrencyConfig?.[currentCurrency]
        : undefined;
    const currentCurrencyDepositConfig = currentCurrency ? currentCurrencyConfig?.deposit : null;
    const currentCurrencyWithdrawConfig = currentCurrency
        ? currentCurrencyConfig?.withdrawal
        : null;
    const currencyDigits = currentCurrency ? currentCurrencyConfig?.digits : undefined;
    const requiredVerificationMethods = useMemo(
        () => ({
            deposit: siteRegistrationConfig?.require_kyc_deposit || [],
            withdraw: siteRegistrationConfig?.require_kyc_withdrawal || [],
        }),
        [
            siteRegistrationConfig?.require_kyc_deposit,
            siteRegistrationConfig?.require_kyc_withdrawal,
        ],
    );

    const minDepositAmount = useMemo(() => {
        if (currentCurrencyDepositConfig) {
            return currentCurrencyDepositConfig.min;
        }
        return 0;
    }, [currentCurrencyDepositConfig]);
    const minWithdrawAmount = useMemo(() => {
        if (currentCurrencyWithdrawConfig) {
            return currentCurrencyWithdrawConfig.min;
        }
        return 0;
    }, [currentCurrencyWithdrawConfig]);

    const maxDepositConfigAmount = useMemo(() => {
        if (currentCurrencyDepositConfig) {
            const maxAmountUsd = currentCurrencyDepositConfig.max_usd;

            return round(maxAmountUsd / usdExchangeRate, currencyDigits);
        }
        return 0;
    }, [currentCurrencyDepositConfig, usdExchangeRate, currencyDigits]);

    const responsibleGamingDepositLimit = useMemo(() => {
        const userPeriodLimits = depositRemainderResponse?.max_period_limits?.[0];
        return userPeriodLimits
            ? round(
                  Number(userPeriodLimits.amount) - Number(userPeriodLimits.actual_sum) || 0,
                  currencyDigits,
              )
            : undefined;
    }, [currencyDigits, depositRemainderResponse?.max_period_limits]);

    const maxDepositAmount = useMemo(() => {
        if (typeof responsibleGamingDepositLimit !== "number") return maxDepositConfigAmount;
        return maxDepositConfigAmount > responsibleGamingDepositLimit
            ? responsibleGamingDepositLimit
            : maxDepositConfigAmount;
    }, [responsibleGamingDepositLimit, maxDepositConfigAmount]);

    const maxWithdrawConfigAmount = useMemo(() => {
        if (currentCurrencyWithdrawConfig) {
            const maxAmountUsd = currentCurrencyWithdrawConfig.max_usd;

            return round(maxAmountUsd / usdExchangeRate, currencyDigits);
        }
        return 0;
    }, [usdExchangeRate, currentCurrencyWithdrawConfig, currencyDigits]);

    const maxWithdrawAmount = useMemo(() => {
        const balance =
            userWallets.data.find(
                (w) => w.type === WalletType.Balance && w.currency === currentCurrency,
            )?.balance || "0";
        return Math.min(Number(balance), maxWithdrawConfigAmount);
    }, [maxWithdrawConfigAmount, userWallets.data, currentCurrency]);

    const decardDepositPaymentMethods = useMemo(
        () =>
            decardPaymentConfig
                ? convertProviderConfig(decardPaymentConfig.deposit, PaymentProvider.Decard)
                : [],
        [decardPaymentConfig],
    );

    const gumBallPayDepositPaymentMethods = useMemo(
        () =>
            gumBallPayPaymentConfig
                ? convertProviderConfig(gumBallPayPaymentConfig.deposit, PaymentProvider.GumBallPay)
                : [],
        [gumBallPayPaymentConfig],
    );

    const decardWithdrawPaymentMethods = useMemo(
        () =>
            decardPaymentConfig
                ? convertProviderConfig(decardPaymentConfig.withdrawal, PaymentProvider.Decard)
                : [],
        [decardPaymentConfig],
    );

    const gumBallPayWithdrawPaymentMethods = useMemo(
        () =>
            gumBallPayPaymentConfig
                ? convertProviderConfig(
                      gumBallPayPaymentConfig.withdrawal,
                      PaymentProvider.GumBallPay,
                  )
                : [],
        [gumBallPayPaymentConfig],
    );

    const depositPaymentMethods = useMemo(
        () =>
            [...decardDepositPaymentMethods, ...gumBallPayDepositPaymentMethods].sort(
                sortAlphabeticallyByProp("order"),
            ),
        [decardDepositPaymentMethods, gumBallPayDepositPaymentMethods],
    );

    const withdrawPaymentMethods = useMemo(
        () =>
            [...decardWithdrawPaymentMethods, ...gumBallPayWithdrawPaymentMethods].sort(
                sortAlphabeticallyByProp("order"),
            ),
        [decardWithdrawPaymentMethods, gumBallPayWithdrawPaymentMethods],
    );

    const [activeView, setActiveView] = useState<MultiWalletViewType>("deposit");
    const [step, setStep] = useState<MultiWalletStepType | MultiWalletDepositStepType>(
        "payment_methods",
    );

    const [depositResponse, setDepositResponse] = useState<IDepositResponse | null>(null);
    const [withdrawResponse, setWithdrawResponse] = useState<IWithdrawalResponse | null>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<DecardPaymentMethod | "">(
        "",
    );
    const [selectedProvider, setSelectedProvider] = useState<PaymentProvider | "">("");

    const amountKeyboardValues = useMemo(
        () => ({
            deposit: walletDepositAmountKeyboardValues.map((val) => ({
                label: val,
                value: val,
            })),
            withdraw: walletWithdrawAmountKeyboardValues.map((val) => ({
                label: val,
                value: val,
            })),
        }),
        [walletDepositAmountKeyboardValues, walletWithdrawAmountKeyboardValues],
    );

    const minAmount = useMemo(
        () => ({ deposit: minDepositAmount, withdraw: minWithdrawAmount }),
        [minDepositAmount, minWithdrawAmount],
    );

    const maxAmount = useMemo(
        () => ({ deposit: maxDepositAmount, withdraw: maxWithdrawAmount }),
        [maxDepositAmount, maxWithdrawAmount],
    );

    const shownDepositCounter =
        activeView === "deposit" &&
        nextUpdateDepositCount > 0 &&
        Number(depositRemainderResponse?.remaining_count) <= 0;

    const shownWithdrawalCounter =
        activeView === "withdraw" &&
        nextUpdateWithdrawalCount > 0 &&
        Number(withdrawalRemainderResponse?.remaining_count) <= 0;

    const isWithdrawalPendingLimit =
        withdrawalRemainderResponse?.pending_limit_count &&
        withdrawalRemainderResponse?.pending_limit_spent
            ? withdrawalRemainderResponse.pending_limit_spent >=
              withdrawalRemainderResponse.pending_limit_count
            : false;

    const rejectedVerificationMethods = useMemo(() => {
        if (documentsLoading) return;

        const filterRejectedMethods = (methods: DocumentType[]) =>
            methods.filter((docType) => {
                if (!docStatus[docType] || docStatus[docType] === DocumentStatus.REJECTED) {
                    return true;
                }
                return false;
            });
        return {
            deposit: filterRejectedMethods(requiredVerificationMethods.deposit),
            withdraw: filterRejectedMethods(requiredVerificationMethods.withdraw),
        };
    }, [docStatus, documentsLoading, requiredVerificationMethods]);

    const onDepositSocketUpdate = useCallback(
        (deposit: IDepositResponse, error: any) => {
            if (error) {
                console.error(error);
                return;
            }

            if (deposit.id !== depositResponse?.id) {
                return;
            }

            if ([DepositStatus.Approved, DepositStatus.Rejected].includes(deposit.status)) {
                setDepositResponse(deposit as unknown as IDepositResponse);
                setStep("status");
            }
        },
        [depositResponse],
    );

    const value = useMemo(
        () => ({
            step,
            setStep,
            activeView,
            depositResponse,
            setDepositResponse,
            setActiveView,
            withdrawResponse,
            setWithdrawResponse,
            onDepositSocketUpdate,
            onClose,
            selectedPaymentMethod,
            setSelectedPaymentMethod,
            selectedProvider,
            setSelectedProvider,
            depositPaymentMethods,
            withdrawPaymentMethods,
            amountKeyboardValues,
            minDepositAmount,
            minAmount,
            maxAmount,
            remainder: {
                shownDepositCounter,
                shownWithdrawalCounter,
                nextUpdateDepositCount,
                nextUpdateWithdrawalCount,
                depositRemainderResponse,
                isWithdrawalPendingLimit,
            },
            rejectedVerificationMethods,
            documentsLoading,
            currentCurrencyConfig,
        }),
        [
            activeView,
            depositPaymentMethods,
            depositResponse,
            onClose,
            onDepositSocketUpdate,
            selectedPaymentMethod,
            selectedProvider,
            step,
            withdrawPaymentMethods,
            withdrawResponse,
            amountKeyboardValues,
            minDepositAmount,
            minAmount,
            maxAmount,
            shownDepositCounter,
            shownWithdrawalCounter,
            nextUpdateDepositCount,
            nextUpdateWithdrawalCount,
            depositRemainderResponse,
            isWithdrawalPendingLimit,
            rejectedVerificationMethods,
            documentsLoading,
            currentCurrencyConfig,
        ],
    );

    return (
        <MultiWalletDataContext.Provider value={value}>{children}</MultiWalletDataContext.Provider>
    );
};
